/**
 * AuthWrapper - Handles MSAL authentication initialization and provides auth gate
 */

import React, { useEffect, useState } from 'react';
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider, useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Spinner } from '@fluentui/react-components';
import { msalConfig, loginRequest } from '../config/MSALConfig';
import { BusyStyles } from '../components/common/Styles';

const Busy = ({ message }: { message: string }) => {
  const styles = BusyStyles();
  return (
    <div className={styles.container}>
      <Spinner size="large" />
      <div>{message}</div>
    </div>
  );
};

export const msalInstance = new PublicClientApplication(msalConfig);

let initPromise: Promise<void> | null = null;

const initializeMsal = async () => {
  if (!initPromise) {
    initPromise = (async () => {
      await msalInstance.initialize();

      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        msalInstance.setActiveAccount(response.account);
      } else {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
      }

      msalInstance.addEventCallback((event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const payload = event.payload as AuthenticationResult;
          msalInstance.setActiveAccount(payload.account);
        }
      });
    })();
  }
  return initPromise;
};

interface AuthGateProps {
  children: React.ReactNode;
}

const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [loginError, setLoginError] = useState<Error | null>(null);

  useEffect(() => {
    const account = instance.getActiveAccount();
    if (!account) {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
      } else if (inProgress === 'none' && !isAuthenticating && !loginError) {
        setIsAuthenticating(true);
        instance.loginRedirect(loginRequest).catch((error) => {
          console.error('Login failed:', error);
          setIsAuthenticating(false);
          setLoginError(error);
        });
      }
    }
  }, [instance, inProgress, isAuthenticating, loginError]);

  if (loginError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px' }}>
        <h2>Sign-In Error</h2>
        <p>Unable to sign in. Please try again or contact support if the issue persists.</p>
        <button onClick={() => { setLoginError(null); setIsAuthenticating(false); }} style={{ marginTop: '16px', padding: '8px 16px' }}>
          Try Again
        </button>
      </div>
    );
  }

  if (inProgress !== 'none' || isAuthenticating) {
    return <Busy message="Checking your sign-in status..." />;
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Busy message="Redirecting to sign in..." />
      </UnauthenticatedTemplate>
    </>
  );
};

interface AuthWrapperProps {
  children: React.ReactNode;
}

export const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);

  useEffect(() => {
    initializeMsal()
      .then(() => {
        setIsInitialized(true);
      })
      .catch((error) => {
        console.error('Failed to initialize MSAL:', error);
        setInitError(error);
      });
  }, []);

  if (initError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px' }}>
        <h2>Authentication Error</h2>
        <p>Failed to initialize authentication. Please refresh the page or contact support if the issue persists.</p>
        <button onClick={() => window.location.reload()} style={{ marginTop: '16px', padding: '8px 16px' }}>
          Refresh Page
        </button>
      </div>
    );
  }

  if (!isInitialized) {
    return <Busy message="Loading..." />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthGate>{children}</AuthGate>
    </MsalProvider>
  );
};
